import { FC, MouseEvent, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Center,
  Flex,
  HStack,
  useRadioGroup,
  VStack,
  Spinner,
  Image,
  useBreakpointValue,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  SystemProps,
} from '@chakra-ui/react'
import { RadioButtonProps } from './interfaces'
import { DefaultCheckbox } from '../checkboxes/default-checkbox'
import { addAnswer, selectAnswers, selectCurrentStep, selectScenarioType } from '../../state'
import { cardImageMap, ScenarioType, Steps } from '../../state/interfaces'
import { useDispatch, useSelector } from 'react-redux'
import { selectScenarioIsWaitingForAnswer } from '../../state'
import { LoadingNewAnswer } from '../spinners/loading-new-answer'
import { useTranslation } from 'react-i18next'

interface Choice {
  value: string
  label: string
  image?: string
  imageSelected?: string
  isChecked?: boolean
  metadata?: any
}

export interface RadioButtonGroupProps {
  name: string
  choices: Choice[]
  value?: string
  onChange?: (values: string[]) => void
  component: FC<RadioButtonProps>
  justify?: SystemProps['justifyContent']
  direction?: 'horizontal' | 'vertical'
  options?: any
  grandImage?: string
}

export const RadioButtonGroup: FC<RadioButtonGroupProps> = ({
  choices,
  value,
  onChange,
  name,
  component: Component,
  direction = 'horizontal',
  options,
  grandImage,
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const currentStep = useSelector(selectCurrentStep)
  const scenarioType = useSelector(selectScenarioType)
  const image = useMemo(() => (grandImage ? cardImageMap[grandImage] : ''), [grandImage])
  const [typeTV, setTypeTV] = useState<string>('basic-tv')
  const [numberTV, setNumberTV] = useState<number>(1)
  const { getRadioProps, getRootProps } = useRadioGroup({
    onChange: (value) => {
      const timeOut = currentStep && currentStep.id === Steps.DECISION_MOBILE_INTERNET ? 350 : 0
      if (currentStep && currentStep.id === Steps.HOW_MANY_CALLING_MINUTES) {
        if (options) {
          dispatch(addAnswer({ stepId: Steps.CALLING_OPTIONS, answerIds: selectedChoices, hidden: true }))
        }
      } else if (currentStep && currentStep.id === Steps.HOW_MUCH_DATA) {
        if (options) {
          dispatch(addAnswer({ stepId: Steps.DATA_OPTIONS, answerIds: selectedChoices, hidden: true }))
        }
      } else if (currentStep && currentStep.id === Steps.CONFIGURATION_TV) {
        dispatch(addAnswer({ stepId: Steps.TV_NUMBER as Steps, answerIds: [numberTV.toString()], hidden: true }))
        dispatch(addAnswer({ stepId: Steps.TV_TYPE as Steps, answerIds: [typeTV], hidden: true }))
      }

      setTimeout(() => {
        return onChange && onChange([value])
      }, timeOut)
    },
    value,
    name,
  })

  useEffect(() => {
    if (currentStep && currentStep.id === Steps.CONFIGURATION_TV) {
      dispatch(addAnswer({ stepId: Steps.TV_TYPE as Steps, answerIds: [typeTV], hidden: true }))
    }
  }, [typeTV])

  const Stack = useMemo<typeof HStack | typeof VStack>(
    () => (direction === 'horizontal' ? HStack : VStack),
    [direction],
  )
  const isDesktop = useBreakpointValue({ base: false, lg: true })
  const [selectedImage, setSelectedImage] = useState('')
  const handleImageClick = (image: string) => {
    setSelectedImage(image)
  }
  const [selectedChoices, setSelectedChoices] = useState<string[]>([])
  const handleNumberTVChange = (_valueAsString: string, valueAsNumber: number) => {
    if (Number.isNaN(valueAsNumber)) {
      setNumberTV(1)
      return
    }

    const boundedValue = Math.min(20, Math.max(1, valueAsNumber))
    setNumberTV(boundedValue)
  }

  const blurActiveElement = () => {
    const activeElement = document.activeElement as HTMLElement | null
    if (activeElement) {
      activeElement.blur()
    }
  }

  const handleCheckboxChange = (value: string, checked: boolean) => {
    if (checked) {
      setSelectedChoices((prevSelected) => [...prevSelected, value])
    } else {
      setSelectedChoices((prevSelected) => prevSelected.filter((val) => val !== value))
    }
  }
  const stepsWithFalseButton = [Steps.START_RATING, Steps.THANKS, Steps.THANKS_COUNTRY]
  const isStepwithFalseButton = currentStep ? stepsWithFalseButton.includes(currentStep.id as Steps) : false

  useEffect(() => {
    if (options) {
      options.forEach((option: any) => {
        if (option.isChecked) {
          setSelectedChoices((prevSelected) => [...prevSelected, option.value])
        }
      })
    }
  }, [])

  const scenarioIsWaitingForAnswer = useSelector(selectScenarioIsWaitingForAnswer)
  const answers = useSelector(selectAnswers)
  const isScenarioVisitor = scenarioType === ScenarioType.CANADA_VISITOR
  const isLastStepFromVisitorScenario = currentStep && currentStep.id === Steps.THANKS && isScenarioVisitor

  return (
    <VStack>
      {(answers &&
        answers['buy-or-bring-phone-quick-search'] &&
        answers['buy-or-bring-phone-quick-search'].includes('finishScenario')) ||
      isLastStepFromVisitorScenario ? (
        isScenarioVisitor ? (
          <Box marginTop={6}>
            <Spinner />
          </Box>
        ) : (
          <Spinner />
        )
      ) : (
        <>
          <Center>
            {options && (
              <VStack>
                {currentStep?.id === Steps.HOW_MUCH_DATA ? (
                  <Flex direction="column" marginTop={5} marginBottom={1} marginLeft={{ base: '5em', md: '7em' }}>
                    <Flex justifyContent="center">
                      {options
                        .filter((option: any) => option.group === 'options')
                        .map((option: any) => (
                          <Box key={option.value} mb={4}>
                            <DefaultCheckbox
                              key={option.value}
                              choice={option}
                              isChecked={option.isChecked}
                              onChange={handleCheckboxChange}
                              type="data"
                            />
                          </Box>
                        ))}
                    </Flex>
                  </Flex>
                ) : (
                  <Flex direction="column" marginTop={5} marginBottom={2}>
                    <Flex direction="column" justifyContent="center">
                      {options
                        .filter((option: any) => option.group === 'options')
                        .map((option: any) => (
                          <Box key={option.value} mb={4}>
                            <DefaultCheckbox
                              key={option.value}
                              choice={option}
                              isChecked={option.isChecked}
                              onChange={handleCheckboxChange}
                            />
                          </Box>
                        ))}
                    </Flex>
                  </Flex>
                )}
              </VStack>
            )}
          </Center>
          <Center>
            <Stack>
              <Flex alignItems="center">
                {isDesktop && image && !isStepwithFalseButton && (
                  <Box marginRight={1}>
                    <div style={{ flex: '60%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Image src={image} alt="image" width="80%" />
                    </div>
                  </Box>
                )}
                <VStack>
                  {currentStep?.id === Steps.CONFIGURATION_TV && (
                    <>
                      <Center
                        width="100%"
                        onClick={(event: MouseEvent<HTMLDivElement>) => event.stopPropagation()}
                        onMouseDown={(event: MouseEvent<HTMLDivElement>) => event.stopPropagation()}
                      >
                        <Select
                          value={typeTV}
                          onChange={(event) => setTypeTV(event.target.value)}
                          onClick={(event: MouseEvent<HTMLSelectElement>) => event.stopPropagation()}
                          onMouseDown={(event: MouseEvent<HTMLSelectElement>) => event.stopPropagation()}
                          border="1px"
                          borderColor="grey"
                          height="7.7vh"
                          maxHeight="70px"
                          width="37vh"
                          maxWidth="298px"
                          margin="15px auto 0"
                          borderRadius="full"
                          fontSize="xs"
                          fontWeight="bold"
                        >
                          <option value="basic-tv">{t('basic-tv')}</option>
                          <option value="add-on-channel">{t('add-on-channel')}</option>
                        </Select>
                      </Center>
                      <Box pt={8} pb={4} fontSize={'18px'}>
                        {t('choose-channels-header')}
                      </Box>
                      <Center
                        width="100%"
                        onClick={(event: MouseEvent<HTMLDivElement>) => event.stopPropagation()}
                        onMouseDown={(event: MouseEvent<HTMLDivElement>) => event.stopPropagation()}
                        pb={5}
                      >
                        <NumberInput
                          min={1}
                          max={20}
                          step={1}
                          value={numberTV}
                          onChange={handleNumberTVChange}
                          focusInputOnChange={false}
                          border="1px"
                          borderColor="grey"
                          height="56px"
                          minHeight="56px"
                          width="120px"
                          minWidth="120px"
                          margin="15px auto 0"
                          borderRadius="10%"
                          keepWithinRange={true}
                          clampValueOnBlur={true}
                        >
                          <NumberInputField
                            readOnly
                            textAlign="center"
                            fontSize="20px"
                            border="0"
                            height="100%"
                            pr="40px"
                            onClick={(event: MouseEvent<HTMLInputElement>) => event.stopPropagation()}
                            onMouseDown={(event: MouseEvent<HTMLInputElement>) => event.stopPropagation()}
                          />
                          <NumberInputStepper width="40px">
                            <NumberIncrementStepper
                              height="50%"
                              fontSize="lg"
                              fontWeight="bold"
                              onMouseUp={blurActiveElement}
                            >
                              +
                            </NumberIncrementStepper>
                            <NumberDecrementStepper
                              height="50%"
                              fontSize="lg"
                              fontWeight="bold"
                              onMouseUp={blurActiveElement}
                            >
                              -
                            </NumberDecrementStepper>
                          </NumberInputStepper>
                        </NumberInput>
                      </Center>
                    </>
                  )}
                  <Stack
                    {...getRootProps()}
                    width={isDesktop && image ? '50%' : '100%'}
                    justifyContent="center"
                    alignItems="center"
                    spacing={[0, 2]}
                    flexWrap="wrap"
                  >
                    {choices.map((props) =>
                      props.image ? (
                        <div key={`choice-${props.value}`} onClick={() => handleImageClick(props.imageSelected || '')}>
                          <Component
                            {...getRadioProps({ value: props.value })}
                            {...props}
                            image={selectedImage === props.imageSelected ? props.imageSelected : props.image}
                          />
                        </div>
                      ) : scenarioIsWaitingForAnswer ? (
                        <LoadingNewAnswer key={`choice-${props.value}`} />
                      ) : (
                        <Component
                          key={`choice-${props.value}`}
                          {...getRadioProps({ value: props.value })}
                          {...props}
                        />
                      ),
                    )}
                  </Stack>
                  {image && isStepwithFalseButton && (
                    <Box marginRight={1}>
                      <div style={{ flex: '60%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Image src={image} alt="image" width={'60%'} height={'60%'} />
                      </div>
                    </Box>
                  )}
                </VStack>
              </Flex>
              <Flex direction="column" alignItems="center" justifyContent="center">
                {/* Other components */}
                {!isDesktop && image && !isStepwithFalseButton && (
                  <Center>
                    <Image src={image} alt="image" width="80%" />
                  </Center>
                )}
              </Flex>
            </Stack>
          </Center>
        </>
      )}
    </VStack>
  )
}
